import {
  GalaxyColumnDef,
  PagedListRequestInterface,
  PagedResponseInterface,
  PaginatedAPIInterface,
} from '@vendasta/galaxy/table';
import {
  catchError,
  combineLatest,
  Observable,
  of,
} from 'rxjs';
import {
  map,
  shareReplay,
  switchMap,
  take,
} from 'rxjs/operators';
import {
  Conversation,
  ConversationSortField,
  MeetingAnalysisApiService,
  PrivacyLevel,
  SortDirection,
} from '@vendasta/meeting-analysis';
import {
  GalaxyDateDefault,
  GalaxyFilterDefinitionInterface,
  GalaxyFilterInputOverride,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
  GalaxyFilterType,
} from '@vendasta/galaxy/filter/chips';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CRMApiService } from '@vendasta/crm';
import { CrmFieldService } from '@galaxy/crm/services/field-service';
import { getFormattedContactDisplayName } from '../users.utility';
import { ListCrmObjectsResponse } from '@vendasta/crm/lib/_internal/objects';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

export interface ConversationRow {
  id: string;
  meetingDate: Date;
  title: string;
  date: string;
  startTime: string;
  duration: string;
  privacyLevel: PrivacyLevel;
  guestCount: number;
  namespace: string;
}

export interface PrivacyLevels {
  Open: string;
  Unlisted: string;
  Private: string;
}

export function PrivacyLevelFilterToValue(privacyLevel: string, PrivacyLevelValues: PrivacyLevels): PrivacyLevel {
  switch (privacyLevel) {
    case PrivacyLevelValues.Open:
      return PrivacyLevel.PRIVACY_LEVEL_OPEN;
    case PrivacyLevelValues.Private:
      return PrivacyLevel.PRIVACY_LEVEL_PRIVATE;
    case PrivacyLevelValues.Unlisted:
      return PrivacyLevel.PRIVACY_LEVEL_UNLISTED;
    default:
      return PrivacyLevel.PRIVACY_LEVEL_UNSPECIFIED;
  }
}

export const ConversationColumns: GalaxyColumnDef[] = [
  {
    id: 'title',
    title: 'MEETING_LIST.TABLE.COLUMNS.TITLE',
  },
  {
    id: 'date',
    title: 'MEETING_LIST.TABLE.COLUMNS.DATE',
  },
  {
    id: 'startTime',
    title: 'MEETING_LIST.TABLE.COLUMNS.START_TIME',
  },
  {
    id: 'duration',
    title: 'MEETING_LIST.TABLE.COLUMNS.DURATION',
  },
  {
    id: 'guestCount',
    title: 'MEETING_LIST.TABLE.COLUMNS.GUESTS',
  },
  {
    id: 'privacyLevel',
    title: 'MEETING_LIST.TABLE.COLUMNS.VISIBILITY',
  },
  {
    id: 'actions',
    stickyEnd: true,
    title: 'MEETING_LIST.TABLE.COLUMNS.ACTIONS',
  },
];

@Injectable()
export class ConversationDataSourceService
  implements PaginatedAPIInterface<ConversationRow, GalaxyFilterInterface, void>
{
  privacyLevelValues$: Observable<PrivacyLevels>;

  conversationFilters: GalaxyFilterDefinitionInterface[] = [
    {
      fieldId: 'keywords',
      type: GalaxyFilterType.FILTER_TYPE_STRING,
      supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_CONTAINS],
      hasFilterOptions: false,
      fieldName: this.translateService.instant('MEETING_LIST.TABLE.FILTERS.KEYWORDS'),
    },
    {
      fieldId: 'privacyLevel',
      type: GalaxyFilterType.FILTER_TYPE_STRING,
      supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_IS],
      hasFilterOptions: true,
      fieldName: this.translateService.instant('MEETING_LIST.TABLE.FILTERS.VISIBILITY'),
    },
    {
      fieldId: 'meetingURL',
      type: GalaxyFilterType.FILTER_TYPE_STRING,
      supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_IS],
      hasFilterOptions: false,
      fieldName: this.translateService.instant('MEETING_LIST.TABLE.FILTERS.MEETING_LINK'),
    },
    {
      fieldId: 'startDate',
      type: GalaxyFilterType.FILTER_TYPE_DATE,
      supportedOperators: [
        GalaxyFilterOperator.FILTER_OPERATOR_IS_AFTER_OR_ON,
        GalaxyFilterOperator.FILTER_OPERATOR_IS_BEFORE,
        GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN,
      ],
      hasFilterOptions: false,
      fieldName: this.translateService.instant('MEETING_LIST.TABLE.FILTERS.DATE'),
    },
    {
      fieldId: 'contact',
      type: GalaxyFilterType.FILTER_TYPE_STRING,
      supportedOperators: [GalaxyFilterOperator.FILTER_OPERATOR_IS],
      hasFilterOptions: true,
      fieldName: this.translateService.instant('MEETING_LIST.TABLE.FILTERS.CONTACT'),
    },
  ];

  private contactNameToIdsMap = new Map<string, string[]>();

  constructor(
    private readonly meetingAnalysisApiService: MeetingAnalysisApiService,
    private readonly translateService: TranslateService,
    private readonly snackbarService: SnackbarService,
    private readonly namespace$: Observable<string>,
    private readonly namespaceName$: Observable<string>,
    private readonly userId$: Observable<string>,
    private readonly contactService: CRMApiService,
    private readonly fieldsService: CrmFieldService,
  ) {
    this.privacyLevelValues$ = this.namespaceName$.pipe(
      map((namespaceName) => {
        return {
          Open: this.translateService.instant('VISIBILITY.OPEN', { namespaceName: namespaceName }),
          Unlisted: this.translateService.instant('VISIBILITY.UNLISTED_V2', { namespaceName: namespaceName }),
          Private: this.translateService.instant('VISIBILITY.PRIVATE'),
        };
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  get(
    request: PagedListRequestInterface<GalaxyFilterInterface, void>,
  ): Observable<PagedResponseInterface<ConversationRow>> {
    return combineLatest([this.namespace$, this.userId$, this.privacyLevelValues$]).pipe(
      take(1),
      switchMap(([namespace, userId, privacyLevelValues]) => {
        const keywords = this.extractKeywordsFromFilters(request.filters || []);
        const privacyLevel = this.extractPrivacyLevelFromFilters(request.filters || [], privacyLevelValues);
        const meetingURL = this.extractMeetingURLFromFilters(request.filters || []);
        const [startTimeAfter, startTimeBefore] = this.extractStartDatesFromFilters(request.filters || []);
        const contactIds = this.extractContactIdsFromFilters(request.filters || []);

        return this.meetingAnalysisApiService.listConversations({
          userId: userId,
          filters: {
            namespace: namespace,
            keywords: keywords,
            privacyLevel: privacyLevel,
            externalId: meetingURL,
            startTimeAfter: startTimeAfter,
            startTimeBefore: startTimeBefore,
            contactIds: contactIds,
          },
          searchTerm: request.searchOptions?.text || '',
          pagingOptions: {
            pageSize: request.pagingOptions?.pageSize || 25,
            cursor: request.pagingOptions?.cursor || '',
          },
          sort: {
            field: ConversationSortField.CONVERSATION_SORT_FIELD_MEETING_DATE,
            direction: SortDirection.SORT_DIRECTION_DESCENDING,
          },
        });
      }),
      map((response) => {
        return {
          data: (response.conversations || []).map((conversation) => this.convertConversationToRow(conversation)),
          pagingMetadata: {
            nextCursor: response.metadata?.nextCursor || '',
            hasMore: response.metadata?.hasMore || false,
            totalResults: response.metadata?.totalResults || 0,
          },
        };
      }),
      catchError(() => {
        this.snackbarService.openErrorSnack('COMMON.ERROR');
        return of({} as PagedResponseInterface<ConversationRow>);
      }),
    );
  }

  private extractMeetingURLFromFilters(filters: GalaxyFilterInterface[]): string {
    for (const filter of filters) {
      if (filter.fieldId === 'meetingURL' && filter.values && filter.values.length > 0) {
        const value = filter.values[0];
        if (value.string) {
          return value.string;
        }
      }
    }
    return '';
  }

  private extractPrivacyLevelFromFilters(
    filters: GalaxyFilterInterface[],
    privacyLevelValues: PrivacyLevels,
  ): PrivacyLevel {
    for (const filter of filters) {
      if (filter.fieldId === 'privacyLevel' && filter.values && filter.values.length > 0) {
        const value = filter.values[0];
        if (value.string) {
          return PrivacyLevelFilterToValue(value.string, privacyLevelValues);
        }
      }
    }
    return PrivacyLevel.PRIVACY_LEVEL_UNSPECIFIED;
  }

  private extractKeywordsFromFilters(filters: GalaxyFilterInterface[]): string[] {
    const keywords: string[] = [];
    for (const filter of filters) {
      if (filter.fieldId === 'keywords' && filter.values) {
        for (const value of filter.values) {
          if (value.string) {
            keywords.push(value.string);
          }
        }
      }
    }
    return keywords;
  }

  // Extract contactIds from filters using the name-to-IDs map
  private extractContactIdsFromFilters(filters: GalaxyFilterInterface[]): string[] {
    for (const filter of filters) {
      if (filter.fieldId === 'contact' && filter.values && filter.values.length > 0) {
        const value = filter.values[0];
        if (value.string) {
          return this.contactNameToIdsMap.get(value.string) || [];
        }
      }
    }
    return [];
  }

  // extractStartDatesFromFilters returns the startDateAfter and startDateBefore from the filters
  private extractStartDatesFromFilters(filters: GalaxyFilterInterface[]): [Date | undefined, Date | undefined] {
    // From the filters derive startDateAfter and startDateBefore
    for (const filter of filters) {
      if (filter.fieldId === 'startDate' && filter.values && filter.values.length > 0) {
        const value = filter.values[0];
        if (value.date) {
          if (filter.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_AFTER_OR_ON) {
            return [value.date, undefined];
          } else if (filter.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BEFORE) {
            return [undefined, value.date];
          } else if (filter.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BETWEEN && filter.values.length > 1) {
            return [value.date, filter.values[1].date];
          }
        }
        if (value.dateDefault) {
          if (filter.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_AFTER_OR_ON) {
            return [this.getDateGivenDateDefault(value.dateDefault), undefined];
          } else if (filter.operator === GalaxyFilterOperator.FILTER_OPERATOR_IS_BEFORE) {
            return [undefined, this.getDateGivenDateDefault(value.dateDefault)];
          }
        }
      }
    }
    return [undefined, undefined];
  }

  // getDateGivenDateDefault takes all the potential values of GalaxyDateDefault and returns the corresponding Date
  private getDateGivenDateDefault(dateDefault: GalaxyDateDefault): Date | undefined {
    const now = new Date();
    switch (dateDefault) {
      case GalaxyDateDefault.DATE_DEFAULT_TODAY: {
        return new Date(now.getFullYear(), now.getMonth(), now.getDate());
      }
      case GalaxyDateDefault.DATE_DEFAULT_YESTERDAY: {
        const yesterday = new Date(now);
        yesterday.setDate(now.getDate() - 1);
        return new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
      }
      case GalaxyDateDefault.DATE_DEFAULT_TOMORROW: {
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        return new Date(tomorrow.getFullYear(), tomorrow.getMonth(), tomorrow.getDate());
      }
      case GalaxyDateDefault.DATE_DEFAULT_THIS_WEEK: {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
        return new Date(now.getFullYear(), now.getMonth(), diff);
      }
      case GalaxyDateDefault.DATE_DEFAULT_LAST_WEEK: {
        const day = now.getDay();
        const diff = now.getDate() - day - 6; // Start of last week (Monday)
        return new Date(now.getFullYear(), now.getMonth(), diff);
      }
      case GalaxyDateDefault.DATE_DEFAULT_NEXT_WEEK: {
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1) + 7; // Start of next week (Monday)
        return new Date(now.getFullYear(), now.getMonth(), diff);
      }
      case GalaxyDateDefault.DATE_DEFAULT_THIS_MONTH: {
        return new Date(now.getFullYear(), now.getMonth(), 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_LAST_MONTH: {
        return new Date(now.getFullYear(), now.getMonth() - 1, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_NEXT_MONTH: {
        return new Date(now.getFullYear(), now.getMonth() + 1, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_THIS_QUARTER: {
        const quarter = Math.floor(now.getMonth() / 3);
        return new Date(now.getFullYear(), quarter * 3, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_LAST_QUARTER: {
        let quarter = Math.floor(now.getMonth() / 3) - 1;
        let year = now.getFullYear();
        if (quarter < 0) {
          quarter = 3;
          year -= 1;
        }
        return new Date(year, quarter * 3, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_NEXT_QUARTER: {
        let quarter = Math.floor(now.getMonth() / 3) + 1;
        let year = now.getFullYear();
        if (quarter > 3) {
          quarter = 0;
          year += 1;
        }
        return new Date(year, quarter * 3, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_THIS_YEAR: {
        return new Date(now.getFullYear(), 0, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_LAST_YEAR: {
        return new Date(now.getFullYear() - 1, 0, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_NEXT_YEAR: {
        return new Date(now.getFullYear() + 1, 0, 1);
      }
      case GalaxyDateDefault.DATE_DEFAULT_INVALID:
      default:
        return undefined;
    }
  }

  private convertConversationToRow(conversation: Conversation): ConversationRow {
    const meetingDate = new Date(conversation.meetingDate);
    const duration = conversation.duration ? Math.floor(parseFloat(conversation.duration)) : 0;

    // Convert milliseconds to hh:mm:ss format
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.floor(duration % 60);
    const formattedDuration = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;

    return {
      id: conversation.id || '',
      namespace: conversation.namespace || '',
      meetingDate: meetingDate,
      title: conversation.title || '',
      date: meetingDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }),
      startTime: meetingDate.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }),
      duration: formattedDuration,
      privacyLevel: conversation.privacyLevel,
      guestCount: (conversation?.internalAttendees?.length || 0) + (conversation?.externalAttendees?.length || 0),
    };
  }

  // listObjectFilters will return the list of filters that can be applied. They should be filtered by the searchTerm
  listObjectFilters(_: string): Observable<GalaxyFilterDefinitionInterface[]> {
    return of(this.conversationFilters);
  }

  // getFieldOptions will return the list of options for a given fieldId.
  // This allows you to autocomplete a filter option for a user.
  getFieldOptions(fieldId: string, search: string): Observable<string[]> {
    if (fieldId === 'privacyLevel') {
      return this.privacyLevelValues$.pipe(
        map((privacyLevelValues) => {
          return [privacyLevelValues.Open, privacyLevelValues.Unlisted, privacyLevelValues.Private];
        }),
      );
    }
    if (fieldId === 'contact') {
      return this.namespace$.pipe(
        switchMap((namespace) => {
          return this.contactService.listContacts({
            namespace: namespace,
            search: { searchTerm: search },
            pagingOptions: {
              pageSize: 50,
            },
          });
        }),
        map((resp: ListCrmObjectsResponse) => {
          this.contactNameToIdsMap.clear();
          resp?.crmObjects?.forEach((contact) => {
            const name = getFormattedContactDisplayName(contact, this.fieldsService);
            const id = contact.crmObjectId;
            if (!this.contactNameToIdsMap.has(name)) {
              this.contactNameToIdsMap.set(name, []);
            }
            this.contactNameToIdsMap.get(name)?.push(id);
          });
          // Return unique names
          return Array.from(this.contactNameToIdsMap.keys());
        }),
        catchError(() => {
          this.snackbarService.openErrorSnack('COMMON.ERROR');
          return of([] as string[]);
        }),
      );
    }
    return of([] as string[]);
  }

  // getInitialAppliedFilters will return the default list of filters that should be displayed.
  // This allows you to have an initial filter state
  getInitialAppliedFilters?(): Promise<GalaxyFilterInterface[]> {
    return Promise.resolve([] as GalaxyFilterInterface[]);
  }

  setInitialAppliedFilters?(_filters: GalaxyFilterInterface[]): void {
    return;
  }

  // getFilterInputOverrides?(): Observable<GalaxyFilterInputOverride[]>;
  // This allows you to override the search box for any field
  // The filter input implementation will still have to abide by the operators allowed for that filter
  getFilterInputOverrides?(): Observable<GalaxyFilterInputOverride[]> {
    return of([] as GalaxyFilterInputOverride[]);
  }
}
